import { Text, View, FlatList, Pressable, Platform } from 'react-native';
import { StyledModal, Title } from '@constants/Themed';
import Modal from 'react-native-modal';
import { useState, useCallback, useEffect, useMemo, forwardRef, useRef, useLayoutEffect, useContext, useImperativeHandle } from 'react';
import styles from '@stylesheet';
import { DefaultRootState, store } from '@reduxLocal/persistState';
import { ADD_NEW_PROJECT, HIDE_PROJECT_MODAL } from '@reduxLocal/actions/types';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import apis from '@api/api';
import { schemaCache } from '@utils/schemaCache';
import LottieView from '@utils/lottieAnimation';
import GenerateSchemaForm from '@hooks/generateSchemaForm';
import BottomSheet, {
  BottomSheetModal,
  BottomSheetView,
} from '@gorhom/bottom-sheet';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import Button from '@components/UI/Button/Button';
import { useFocusEffect } from '@react-navigation/native';
import DataSetChart from '@components/dataTables/Charts/DataSetChart';
import { ToolbarContext } from '@hooks/toolbarContext';
import { YStack } from 'tamagui';
import { useDeviceType } from '@hooks/useDeviceType';

interface AddProjectModalProps {
  callBack: () => void;
  notContext?: boolean;
  queryKey?: string;
}

interface ProjectModalHandle {
  openModal: () => void,
  closeModal: () => void,
  present: () => void,
  dismiss: () => void
}

const AddProjectModal = forwardRef<ProjectModalHandle, AddProjectModalProps>((props, ref) => {
  // Use cached schema instead of fetching
  const projectSchemaData = schemaCache.getSchema('projectSchema');
  const theme = useSelector((state: DefaultRootState) => state.app.theme.lightTheme);
  const addProjectModalDisplayed = useSelector((state: DefaultRootState) => state.modals.addProjectModalDisplayed);
  const addProjectModalData = useSelector((state: DefaultRootState) => state.modals.addProjectModalData);
  const queryClient = useQueryClient();
  const addProjectModalRef = useRef(null);
  const { projectContext } = useContext(ToolbarContext);
  const bottomSheetRef = useRef<BottomSheetModal>(null);
  const { isTablet } = useDeviceType();

  const dispatch = useDispatch();

  const snapPoints = useMemo(() => [
    '25%',
    Platform.select({ web: '70%', default: '90%' })
  ], []);

  // Expose methods to parent via ref
  useImperativeHandle(ref, () => ({
    openModal: () => {
      if (Platform.OS === 'web') {
        addProjectModalRef.current?.openModal?.();
      } else {
        bottomSheetRef.current?.present?.();
        // Snap to the larger snap point (index 1) for better interactivity
        setTimeout(() => {
          bottomSheetRef.current?.snapToIndex?.(1);
        }, 100);
      }
    },
    closeModal: () => {
      if (Platform.OS === 'web') {
        addProjectModalRef.current?.closeModal?.();
      } else {
        bottomSheetRef.current?.dismiss?.();
      }
    },
    present: () => {
      bottomSheetRef.current?.present?.();
      // Snap to the larger snap point (index 1) for better interactivity
      setTimeout(() => {
        bottomSheetRef.current?.snapToIndex?.(1);
      }, 100);
    },
    dismiss: () => {
      bottomSheetRef.current?.dismiss?.();
    }
  }), []);

  const addProject = (value) => {
    if (Platform.OS != 'web') {
      bottomSheetRef.current?.dismiss?.();
    }
    if (projectContext) {
    projectContext.refreshFn?.();
    }
    dispatch({ type: ADD_NEW_PROJECT, log: true });
    props.callBack();
  }


  // Listen to Redux state to open/close modal
  useEffect(() => {
    if (addProjectModalDisplayed) {
      // Open the modal
      if (Platform.OS === 'web') {
        addProjectModalRef.current?.openModal?.();
      } else {
        bottomSheetRef.current?.present?.();
        // Snap to the larger snap point (index 1) for better interactivity
        setTimeout(() => {
          bottomSheetRef.current?.snapToIndex?.(1);
        }, 100);
      }
    } else {
      // Close the modal
      if (Platform.OS === 'web') {
        addProjectModalRef.current?.closeModal?.();
      } else {
        bottomSheetRef.current?.dismiss?.();
      }
    }
  }, [addProjectModalDisplayed]);

  // Hide modal when screen loses focus
  useFocusEffect(
    useCallback(() => {
      // When screen is focused, do nothing (modal can be opened)
      return () => {
        // When screen loses focus, dismiss the modal
        if (Platform.OS !== 'web') {
          bottomSheetRef.current?.dismiss?.();
        } else {
          addProjectModalRef.current?.closeModal?.();
        }
      };
    }, [])
  );

  // callbacks
  const handleSheetChanges = useCallback((index: number) => {
  }, []);

  if (Platform.OS != 'web') {
    return (
      <BottomSheetModal
        ref={bottomSheetRef}
        snapPoints={snapPoints}
        onChange={handleSheetChanges}
        enablePanDownToClose={true}
        enableDismissOnClose={true}
        enableDynamicSizing={false}
        animateOnMount={true}
        handleIndicatorStyle={{ backgroundColor: theme ? 'black' : 'white' }}
        backgroundStyle={{ backgroundColor: theme ? 'white' : 'grey' }}
        style={[styles.bottomSheet, { backgroundColor: theme ? '#ececec' : '#333333' }]}
      >
        <BottomSheetView style={{ flex: 1 }}>
          <View style={{ flex: 1 }}>
            <Title style={{ textAlign: "center" }}>Create a New Project</Title>
            <View style={{ flex: 1, padding: 20, paddingLeft: "5%" }}>
              <GenerateSchemaForm
                scrollEnabled
                schema={projectSchemaData}
                buttonLabel='Add Project'
                customStyling={{ paddingBottom: "0%", maxHeight: "90%" }}
                submitFunction={apis.addProjects}
                nullify={['work_packages', 'companyId', 'start_date', 'end_date', 'team', 'createdBy', 'status', 'tasks']}
                avoidKeyboardDisabled
                addOffset={80}
                extraFunction={addProject}
                initialData={addProjectModalData}
              />
            </View>
          </View>
        </BottomSheetView>
      </BottomSheetModal>
    )
  }
  //TODO Ensure correct usage of query
  else return (
    <>
      {
        props.notContext ?
          <StyledModal
            onModalWillHide={() => {
              queryClient.invalidateQueries([{ queryKey: 'projectData-fallback' }])
            }}
            title='Add a new project' caption='Making a new project is easy, just enter the details of your project on the right and voila!' ref={ref}>
            <YStack paddingTop={50} flex={1}>
              <GenerateSchemaForm
                scrollEnabled
                schema={projectSchemaData}
                buttonLabel='Add Project'
                extraCallBack={() => ref.current!.closeModal()}
                submitFunction={apis.addProjects}
                nullify={['work_packages', 'start_date', 'companyId', 'end_date', 'team', 'createdBy', 'status', 'tasks']}
                sheetRef={addProjectModalRef}
                noBackground
                extraFunction={addProject}
                initialData={addProjectModalData}
              />
            </YStack>
          </StyledModal>
          :
          <>
            <StyledModal title='Add a new project' caption='Making a new project is easy, just enter the details of your project on the right and voila!' ref={ref}>
              <GenerateSchemaForm
                Progress
                schema={projectSchemaData}
                buttonLabel='Add Project'
                extraCallBack={() => {
                  addProjectModalRef.current!.closeModal()
                  queryClient.invalidateQueries({ queryKey: [props.queryKey] })
                  props.callBack()
                }}
                submitFunction={apis.addProjects}
                nullify={['work_packages', 'start_date', 'end_date', 'team', 'createdBy', 'status', 'tasks']}
                sheetRef={addProjectModalRef}
                noBackground
                avoidKeyboardDisabled
                extraFunction={addProject}
                initialData={addProjectModalData}
              />
            </StyledModal>
            <DataSetChart
              chartType={"text"}
              title={"Project Stats"}
              subheader={""}
              dotLabel={"Projects"}
              chart={{
                labels: ['Projects'],
                colors: ['#00ff00', 'blue', 'red', 'grey'],
                series: [
                  {
                    name: 'Completed',
                    type: 'column',
                    fill: 'fill',
                    data: [23],
                  },
                  {
                    name: 'Active',
                    type: 'column',
                    fill: 'fill',
                    data: [13],
                  },
                  {
                    name: 'Overdue',
                    type: 'column',
                    fill: 'fill',
                    data: [22],
                  },
                  {
                    name: 'Pending',
                    type: 'column',
                    fill: 'fill',
                    data: [17],
                  }
                ]
              }} />
            <Button buttonText={"Add New Project"} textColor='white' onPress={() => addProjectModalRef.current?.openModal?.()} />
          </>
      }
    </>
  )
})

export default AddProjectModal;